import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as THREE from 'three';
import Header from '@/components/Header.jsx';
import Footer from '@/components/Footer.jsx';
import Seo from '@/components/Seo.jsx';
const partners = [{
  id: 1,
  name: "Collectif Plaisance",
  logo: "https://horizons-cdn.hostinger.com/257434f3-17c6-40ce-944a-86edaf1db41d/215ec96f75e2cf1543cbfbd9e344c52e.png",
  message: "Un grand merci de nous accueillir et nous faire profiter des salles communales."
}, {
  id: 2,
  name: "Donjon du Poulpe Galactique",
  logo: "/dpg.jpg",
  message: "Les grands Anciens des tables de jeu de rôle Sautronnaises, une rencontre qui a fait naître notre projet."
}];

// Petite croix "pixel art" utilisée pour les étincelles
const PixelSparkle = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 12 12" aria-hidden="true">
    <path d="M4 0h4v4h4v4H8v4H4V8H0V4h4z" fill="#ffffff" />
  </svg>
);

const ROLL_DURATION = 1.1; // secondes

const ThreeD20 = ({
  onClick,
  isRolling
}) => {
  const mountRef = useRef(null);
  const meshRef = useRef(null);
  const rollVelocity = useRef(new THREE.Vector3());
  const [sparkles, setSparkles] = useState([]);
  useEffect(() => {
    let scene, camera, renderer, mesh, edges, glowMesh, animationId;
    let isMounted = true;
    const init = () => {
      try {
        if (!isMounted) return;

        // Setup
        const width = 240;
        const height = 240;
        scene = new THREE.Scene();
        camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
        camera.position.z = 5;
        renderer = new THREE.WebGLRenderer({
          alpha: true,
          antialias: true
        });
        renderer.setSize(width, height);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        if (mountRef.current) {
          mountRef.current.innerHTML = '';
          mountRef.current.appendChild(renderer.domElement);
        }

        // --- Texture holographique avec trame pixel art ---
        const canvas = document.createElement('canvas');
        canvas.width = 256;
        canvas.height = 256;
        const ctx = canvas.getContext('2d');
        const gradient = ctx.createLinearGradient(0, 0, 256, 256);
        gradient.addColorStop(0, '#8B45D6'); // Violet y2k
        gradient.addColorStop(0.35, '#D44B8A');
        gradient.addColorStop(0.6, '#EF509D'); // Rose signature
        gradient.addColorStop(0.8, '#F3A0C8');
        gradient.addColorStop(1, '#7B3FA8');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, 256, 256);

        // Reflet diagonal façon chrome
        const sheen = ctx.createLinearGradient(0, 256, 256, 0);
        sheen.addColorStop(0.4, 'rgba(255,255,255,0)');
        sheen.addColorStop(0.5, 'rgba(255,255,255,0.45)');
        sheen.addColorStop(0.6, 'rgba(255,255,255,0)');
        ctx.fillStyle = sheen;
        ctx.fillRect(0, 0, 256, 256);

        // Damier pixel art subtil
        const px = 8;
        for (let y = 0; y < 256; y += px) {
          for (let x = 0; x < 256; x += px) {
            if ((x / px + y / px) % 2 === 0) {
              ctx.fillStyle = 'rgba(255,255,255,0.06)';
              ctx.fillRect(x, y, px, px);
            }
          }
        }
        const texture = new THREE.CanvasTexture(canvas);
        texture.magFilter = THREE.NearestFilter; // rendu net, esprit pixel

        // Geometry
        const geometry = new THREE.IcosahedronGeometry(1.4, 0);

        // Matériau iridescent y2k
        const material = new THREE.MeshPhysicalMaterial({
          map: texture,
          metalness: 0.85,
          roughness: 0.2,
          flatShading: true,
          clearcoat: 1,
          clearcoatRoughness: 0.15,
          iridescence: 0.8,
          iridescenceIOR: 1.6
        });
        mesh = new THREE.Mesh(geometry, material);
        meshRef.current = mesh;
        scene.add(mesh);

        // Arêtes nettes blanches
        const edgesGeo = new THREE.EdgesGeometry(geometry);
        const edgesMat = new THREE.LineBasicMaterial({
          color: 0xffffff,
          transparent: true,
          opacity: 0.85
        });
        edges = new THREE.LineSegments(edgesGeo, edgesMat);
        mesh.add(edges);

        // Halo rose additif autour du dé
        const glowGeo = new THREE.IcosahedronGeometry(1.55, 0);
        const glowMat = new THREE.MeshBasicMaterial({
          color: 0xef509d,
          transparent: true,
          opacity: 0.16,
          side: THREE.BackSide,
          blending: THREE.AdditiveBlending,
          depthWrite: false
        });
        glowMesh = new THREE.Mesh(glowGeo, glowMat);
        mesh.add(glowMesh);

        // Lighting
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
        scene.add(ambientLight);
        const dirLight = new THREE.DirectionalLight(0xffffff, 1.4);
        dirLight.position.set(5, 5, 5);
        scene.add(dirLight);
        const pinkLight = new THREE.PointLight(0xef509d, 4, 12);
        pinkLight.position.set(-3, -2, 3);
        scene.add(pinkLight);
        const purpleLight = new THREE.PointLight(0x8b45d6, 4, 12);
        purpleLight.position.set(3, 2, -2);
        scene.add(purpleLight);

        // Animation Loop
        const clock = new THREE.Clock();
        const animate = () => {
          if (!isMounted) return;
          animationId = requestAnimationFrame(animate);
          const time = clock.getElapsedTime();
          const m = meshRef.current;
          if (m) {
            if (m.userData.isRolling) {
              if (m.userData.rollPending) {
                m.userData.rollStart = time;
                m.userData.rollPending = false;
              }
              const p = Math.min((time - (m.userData.rollStart ?? time)) / ROLL_DURATION, 1);

              // Rotation rapide qui décélère naturellement
              m.rotation.x += rollVelocity.current.x;
              m.rotation.y += rollVelocity.current.y;
              m.rotation.z += rollVelocity.current.z;
              rollVelocity.current.multiplyScalar(0.965);

              // Saut en cloche + léger squash à la retombée
              m.position.y = Math.sin(p * Math.PI) * 0.55;
              const squash = 1 + Math.sin(p * Math.PI) * 0.08;
              m.scale.set(squash, squash, squash);

              // Halo plus intense pendant le lancer
              glowMat.opacity = 0.16 + Math.sin(p * Math.PI) * 0.18;
            } else {
              // Flottement / respiration au repos
              m.position.y = Math.sin(time * 1.8) * 0.1;
              const scale = 1 + Math.sin(time * 2.6) * 0.03;
              m.scale.set(scale, scale, scale);
              m.rotation.x += 0.004;
              m.rotation.y += 0.009;
              glowMat.opacity = 0.13 + Math.sin(time * 2.2) * 0.05;
            }
          }
          renderer.render(scene, camera);
        };
        animate();
      } catch (error) {
        console.error("Failed to initialize Three.js scene", error);
      }
    };
    init();
    return () => {
      isMounted = false;
      if (animationId) cancelAnimationFrame(animationId);
      if (renderer && mountRef.current) {
        mountRef.current.innerHTML = '';
        renderer.dispose();
      }
      if (mesh) {
        mesh.geometry.dispose();
        mesh.material.dispose();
      }
      if (edges) {
        edges.geometry.dispose();
        edges.material.dispose();
      }
      if (glowMesh) {
        glowMesh.geometry.dispose();
        glowMesh.material.dispose();
      }
    };
  }, []);

  // Sync rolling state to Three.js mesh
  useEffect(() => {
    const m = meshRef.current;
    if (!m) return;
    m.userData.isRolling = isRolling;
    if (isRolling) {
      m.userData.rollPending = true;
      // Impulsion aléatoire pour un lancer différent à chaque fois
      rollVelocity.current.set(
        0.22 + Math.random() * 0.18,
        0.28 + Math.random() * 0.22,
        0.12 + Math.random() * 0.14
      );
    }
  }, [isRolling]);
  const handleClick = () => {
    if (isRolling) return;

    // Étincelles pixel art projetées autour du dé
    const burst = Array.from({ length: 8 }, (_, i) => {
      const angle = (i / 8) * Math.PI * 2 + Math.random() * 0.5;
      const distance = 90 + Math.random() * 50;
      return {
        id: `${Date.now()}-${i}`,
        dx: Math.cos(angle) * distance,
        dy: Math.sin(angle) * distance,
        size: 8 + Math.round(Math.random() * 10),
        delay: Math.random() * 0.15
      };
    });
    setSparkles(burst);
    setTimeout(() => setSparkles([]), 1000);
    onClick();
  };
  return <div className="relative cursor-pointer w-60 h-60 mx-auto flex items-center justify-center rounded-full group" onClick={handleClick}>
      {/* Halo lumineux derrière le dé */}
      <div className={`absolute inset-4 rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.55)_0%,rgba(239,80,157,0.35)_45%,transparent_70%)] blur-2xl transition-opacity duration-300 ${isRolling ? 'opacity-100' : 'opacity-60'}`} />

      {/* Three.js Canvas Container */}
      <div ref={mountRef} className="absolute inset-0 flex items-center justify-center z-10 drop-shadow-2xl transition-transform duration-300 group-hover:scale-105" />

      {/* Étincelles pixel art */}
      <AnimatePresence>
        {sparkles.map(s => <motion.div key={s.id} initial={{
        x: 0,
        y: 0,
        scale: 0,
        opacity: 1
      }} animate={{
        x: s.dx,
        y: s.dy,
        scale: [0, 1.2, 0.8],
        opacity: [1, 1, 0],
        rotate: 90
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.7,
        delay: s.delay,
        ease: "easeOut"
      }} className="absolute z-20 pointer-events-none drop-shadow-[0_0_6px_rgba(255,255,255,0.9)]">
            <PixelSparkle size={s.size} />
          </motion.div>)}
      </AnimatePresence>
    </div>;
};
function PartnersPage() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isRolling, setIsRolling] = useState(false);
  const handleDiceClick = () => {
    if (isRolling) return;
    setIsRolling(true);

    // Change partner halfway through the roll animation
    setTimeout(() => {
      setActiveIndex(prev => (prev + 1) % partners.length);
    }, 550);
    setTimeout(() => {
      setIsRolling(false);
    }, ROLL_DURATION * 1000);
  };
  const activePartner = partners[activeIndex];
  return <>
      <Seo
        title="Partenaires - Neuvième Continent"
        description="Découvrez les partenaires du Neuvième Continent qui rendent nos aventures possibles."
      />

      <div className="min-h-screen flex flex-col bg-background">
        <Header />

        <main className="flex-grow">
          {/* Hero Section with Gradient */}
          <section className="relative min-h-[100dvh] flex flex-col items-center justify-center py-24 px-4 sm:px-6 lg:px-8 overflow-hidden" style={{
          background: 'linear-gradient(to bottom, #EF509D, #f27277)'
        }}>
            <div className="max-w-5xl mx-auto relative z-10 w-full flex flex-col items-center">

              <motion.div initial={{
              opacity: 0,
              y: -20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} className="text-center mb-12">
                <h1 className="font-pixel text-5xl md:text-6xl lg:text-7xl text-white mb-6 drop-shadow-lg tracking-wide">
                  Partenariat
                </h1>
                <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto leading-relaxed font-medium drop-shadow-md">
                  Lance le dé pour découvrir ceux qui rendent nos aventures possibles.
                </p>
              </motion.div>

              {/* Interactive 3D Dice */}
              <div className="mb-12">
                <ThreeD20 onClick={handleDiceClick} isRolling={isRolling} />
                <p className="text-white/80 text-sm text-center mt-2 font-bold uppercase tracking-widest animate-pulse">
                  Cliquez pour lancer
                </p>
              </div>

              {/* Partner Display Area */}
              <div className="w-full max-w-3xl h-[420px] sm:h-[400px] md:h-[450px] relative">
                <AnimatePresence mode="wait">
                  <motion.div key={activePartner.id} initial={{
                  opacity: 0,
                  scale: 0.95,
                  y: 20
                }} animate={{
                  opacity: 1,
                  scale: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  scale: 0.95,
                  y: -20
                }} transition={{
                  duration: 0.4,
                  ease: "easeOut"
                }} className="absolute inset-0 bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl p-6 sm:p-8 md:p-12 shadow-2xl flex flex-col items-center justify-center text-center overflow-hidden">
                    <div className="w-24 h-24 sm:w-32 sm:h-32 md:w-48 md:h-48 mb-4 sm:mb-6 md:mb-8 relative flex items-center justify-center bg-white rounded-2xl p-3 sm:p-4 md:p-6 shadow-inner flex-shrink-0">
                      <img src={activePartner.logo} alt={`Logo de ${activePartner.name}`} className="max-w-full max-h-full object-contain" />
                    </div>
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2 sm:mb-4 drop-shadow-md">
                      {activePartner.name}
                    </h2>
                    <p className="text-base sm:text-lg md:text-xl text-white/90 leading-relaxed max-w-xl line-clamp-4 sm:line-clamp-none">
                      "{activePartner.message}"
                    </p>
                  </motion.div>
                </AnimatePresence>
              </div>

            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>;
}
export default PartnersPage;